// Card #3308 -- the screen-transition state machine for the board's CONTENT
// area. It decides what ScoreboardFrame renders inside `.scoreboard-frame__content`
// and which phase class that element carries; the footer bar (clock + QR +
// logo) is a sibling of that element and never reads any of this.
//
// Phases:
//   idle   the screen on show, fully visible. The only resting state.
//   out    (fade) the OLD screen's content, frozen, fading to the theme
//          background. After durationMs it flips to "in".
//   in     (fade) the NEW screen's live content fading up from the background.
//   cross  (crossfade) the new content fades in while the old content, frozen
//          in an overlay, fades out on top of it.
//
// Every non-idle phase is bounded by ONE timer of durationMs that ends in
// idle, and the timer is only ever (re)armed when a phase starts, so a burst of
// screen changes cannot stretch a transition out or leave it half-faded:
//   - a change that lands mid-"out" just retargets: the board fades back in on
//     the LATEST screen when the timer fires;
//   - a change that lands mid-"in"/"cross" starts a fresh transition from
//     whatever is on screen.
// `content` is always the live node for the current screen, so a value updating
// inside the same screen (same key) re-renders in place with no phase change.
// A payload that fails or never arrives just means `content` is the Blank+logo
// fallback; the phase still ends in idle on its own timer.
//
// `screenKey === undefined` turns the whole thing off (the theme editor's
// embedded preview): content passes straight through.
import { useEffect, useRef, useState } from "react";
import { LOADING_SCREEN_KEY, pickTransition } from "./screenTransition";

export const TRANSITION_PHASE = {
  IDLE: "idle",
  OUT: "out",
  IN: "in",
  CROSS: "cross",
};

export function useScreenTransition({ screenKey, themeTransition, slideshowTransition, content }) {
  const enabled = screenKey !== undefined;
  const [state, setState] = useState({
    key: screenKey,
    phase: TRANSITION_PHASE.IDLE,
    outgoing: null,
    durationMs: 0,
    seq: 0,
    // Which live layer is on show. Only a crossfade bumps it: the old layer
    // keeps its old key (so React keeps the SAME element, never remounting the
    // frozen screen) and the new screen mounts under the next key.
    layer: 0,
  });
  // The content of the screen that is on show right now (not an outgoing
  // snapshot): what a new transition freezes as its "old" side.
  const onShowRef = useRef(content);

  let current = state;
  if (enabled && screenKey !== state.key) {
    // Adjusting state while rendering (React's documented pattern for state
    // derived from a changing prop): the next render uses `next`.
    const isFirstScreen = state.key === undefined || state.key === LOADING_SCREEN_KEY;
    const { type, durationMs } = pickTransition({
      prevKey: state.key,
      nextKey: screenKey,
      themeTransition,
      slideshowTransition,
    });
    let next;
    if (isFirstScreen || type === "cut") {
      next = { ...state, key: screenKey, phase: TRANSITION_PHASE.IDLE, outgoing: null, durationMs: 0, seq: state.seq + 1 };
    } else if (state.phase === TRANSITION_PHASE.OUT) {
      next = { ...state, key: screenKey };
    } else if (type === "crossfade") {
      next = {
        key: screenKey,
        phase: TRANSITION_PHASE.CROSS,
        outgoing: onShowRef.current,
        durationMs,
        seq: state.seq + 1,
        layer: state.layer + 1,
      };
    } else {
      next = {
        ...state,
        key: screenKey,
        phase: TRANSITION_PHASE.OUT,
        outgoing: onShowRef.current,
        durationMs,
        seq: state.seq + 1,
      };
    }
    setState(next);
    current = next;
  }

  const phase = enabled ? current.phase : TRANSITION_PHASE.IDLE;
  if (phase !== TRANSITION_PHASE.OUT) onShowRef.current = content;

  useEffect(() => {
    if (phase === TRANSITION_PHASE.IDLE) return undefined;
    const timer = setTimeout(() => {
      setState((s) =>
        s.phase === TRANSITION_PHASE.OUT
          ? { ...s, phase: TRANSITION_PHASE.IN, outgoing: null }
          : { ...s, phase: TRANSITION_PHASE.IDLE, outgoing: null }
      );
    }, current.durationMs);
    return () => clearTimeout(timer);
    // The timer belongs to one phase of one transition (seq): a retarget during
    // "out" changes `key` only, so it does not re-arm and stretch the fade.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, current.seq]);

  if (!enabled) return { phase, durationMs: 0, content, outgoing: null, layer: 0 };
  return {
    phase,
    durationMs: current.durationMs,
    // During "out" the frozen OLD content is what shows; every other phase
    // shows the live content.
    content: phase === TRANSITION_PHASE.OUT ? current.outgoing : content,
    // Only a crossfade overlays the old content on top of the new.
    outgoing: phase === TRANSITION_PHASE.CROSS ? current.outgoing : null,
    // The key of the live layer; the outgoing one (crossfade) is layer - 1.
    layer: current.layer,
  };
}
